import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { createSliceFoundation, getBaseImpures, getBaseReducers, PropsFrom } from "@crispico/foundation-react/reduxHelpers";
import { apolloClientHolder } from "@crispico/foundation-react/apolloClient";
import gql from "graphql-tag";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import React from "react";
import { ModalExt, ModalExtOpen } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { Dimmer, Icon, Loader, Modal, Table } from "semantic-ui-react";
import { Optional } from "@crispico/foundation-react/CompMeta";
import { FieldDescriptor, EntityDescriptor, TabDescriptor } from "../../entity_crud/EntityDescriptor";
import { FieldType, CUSTOM_FIELDS } from "../../entity_crud/FieldType";
import Interweave from "interweave";
import { ActionName, TRUE_VALUES, AuditUtils } from "./AuditUtils";
import { NavLink } from "react-router-dom";
import { AuditFieldRenderer, sliceAuditFieldRenderer } from "./AuditFieldRenderer";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { ShareLinkLogic } from "@crispico/foundation-react/entity_crud/ShareLinkLogic";

var maxRowsForPopup = 20;

interface AuditRow {
    username: string,
    date: Date,
    newValue: string
}

export const sliceAuditEntriesPopup = createSliceFoundation(class SliceAuditEntriesPopup {
    initialState = { entityName: "", fieldName: "", entityId: 0, open: false as ModalExtOpen, auditRows: [] as AuditRow[], showSpinner: false }

    nestedSlices = {
        auditFieldRenderer: sliceAuditFieldRenderer
    }

    reducers = {
        ...getBaseReducers<SliceAuditEntriesPopup>(this),
    }

    impures = {
        ...getBaseImpures<SliceAuditEntriesPopup>(this),
        async getAuditRows(entityName: string, fieldName: string, entityId: number) {
            const { auditSettings } = AppMetaTempGlobals.appMetaInstance.helperAppContainer.dispatchers.getState().initializationsForClient;
            if (auditSettings) {
                maxRowsForPopup = auditSettings.maxRowsForPopup ? auditSettings.maxRowsForPopup : maxRowsForPopup;
            }

            const query = gql(`query q($params: FindByFilterParamsInput) {
                auditService_findByFilter(params: $params) {
                  results {
                    username date newValue
                  }
                }
            }`);

            const params = FindByFilterParams.create().pageSize(maxRowsForPopup).sorts([{ field: "date", direction: "DESC" }])
                .filter(Filter.createComposed(FilterOperators.forComposedFilter.and, [
                    AuditUtils.getFilterForAuditableEntity(entityName), AuditUtils.getFilterForAuditableField(entityName, fieldName),
                    Filter.create("entityId", FilterOperators.forNumber.equals, entityId.toString()),
                    Filter.createComposed(FilterOperators.forComposedFilter.or, [ 
                        Filter.create("action", FilterOperators.forString.equals, ActionName.UPDATE),
                        Filter.create("action", FilterOperators.forString.equals, ActionName.REFRESH),
                    ])
            ]));

            const result: AuditRow[] = (await apolloClientHolder.apolloClient.query({ query, variables: params })).data.auditService_findByFilter.results;
            this.getDispatchers().setInReduxState({ auditRows: result, showSpinner: false })
        },
        async open(entityName: string, fieldName: string, entityId: number, open: ModalExtOpen) {
            this.getDispatchers().setInReduxState({ entityName, fieldName, entityId, open, auditRows: [], showSpinner: true })
            await this.getAuditRows(entityName, fieldName, entityId);
        }
    }
})

export class AuditEntriesPopup extends React.Component<PropsFrom<typeof sliceAuditEntriesPopup>> {
    render() {
        const { props } = this;

        try {
            const ed: EntityDescriptor = entityDescriptors[props.entityName];
            const auditTab: Optional<TabDescriptor> = ed?.tabDescriptors?.find(x => x.oneToManyEntityName === "Audit");
            const fd: Optional<FieldDescriptor> = props.entityName ? ed?.getField(props.fieldName) : undefined;

            // accessing the ED this way, to avoid circular imports issue
            const auditUsernameFd = entityDescriptors["Audit"].getField("username");
            const auditDateFd = entityDescriptors["Audit"].getField("date");
            const filterForAuditTable = Filter.createComposed(FilterOperators.forComposedFilter.and, [AuditUtils.getFilterForAuditableEntity(props.entityName, true),
                Filter.create("entityId", FilterOperators.forNumber.equals, props.entityId.toString(), true)], true)
            
            return <>
                <ModalExt open={props.open} size='tiny' onClose={() => props.dispatchers.setInReduxState({ open: false })} >
                    <Modal.Header>
                        <Icon name="chart line"/>
                        <Interweave content={_msg("AuditEntriesPopup.tableHeader", fd?.getLabel())} />
                    </Modal.Header>
                    <Modal.Content scrolling>
                        <Table striped>
                            <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{_msg("Audit.username.label")}</Table.HeaderCell>
                                <Table.HeaderCell>{_msg("Audit.date.label")}</Table.HeaderCell>
                                <Table.HeaderCell>{_msg("Audit.newValue.label")}</Table.HeaderCell>
                            </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {props.auditRows.map((auditRow, index) => {
                                        const value = fd && { [fd.name]: fd?.type === FieldType.boolean ? TRUE_VALUES.includes(auditRow.newValue) : auditRow.newValue }
                                        const simulatedEntity = fd ? (fd.isCustomField ? { [CUSTOM_FIELDS]: value } : value) : {};
                                        return (
                                            <Table.Row key={index}>
                                                <Table.Cell>{auditUsernameFd.renderField(auditRow)}</Table.Cell>
                                                <Table.Cell>{auditDateFd.renderField(auditRow)}</Table.Cell>
                                                <Table.Cell><AuditFieldRenderer fd={fd} entity={simulatedEntity} dispatchers={this.props.dispatchers.auditFieldRenderer}/></Table.Cell>
                                            </Table.Row>
                                        );
                                    }
                                )}
                            </Table.Body>
                        </Table>
                        <Dimmer active={this.props.showSpinner}><Loader size='large'>Loading</Loader></Dimmer>
                        <>
                            {_msg("AuditEntriesPopup.tableFooter", maxRowsForPopup)}&nbsp;
                            {auditTab ? <NavLink to={ed?.getEntityEditorUrl(props.entityId) + auditTab?.tabRouterPane?.routeProps?.path}>{auditTab?.nestedSliceFieldName}</NavLink> : 
                            <NavLink to={new ShareLinkLogic().createLink(false, entityDescriptors["Audit"], filterForAuditTable, [])}>{_msg("Audit.label")}</NavLink> }
                        </>
                    </Modal.Content>
                </ModalExt>
            </>
        } catch (e) {
            return <>{_msg("general.error")}</>
        }
    }
}